import { Button } from "@/shared/components/ui/button";
import { Checkbox } from "@/shared/components/ui/checkbox";
import { Label } from "@/shared/components/ui/label";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/shared/components/ui/card";
import { PlusIcon } from "lucide-react";
import { useReportFilters } from "./hooks/use-report-filters";
import { ReportCard } from "./components/report-card";
import { useQuery } from "@tanstack/react-query";
import { reportQueries } from "../../queries";
import { FilterDropdown } from "./components/filter-dropdown";
import { CreateReportDialog } from "../../components/create-report-dialog";
import { FilterBar } from "@/shared/components/filter-bar";
import { useTenant } from "@/domains/tenant/hooks";

export function ReportsPage() {
  const { data: tenantData } = useTenant();
  const {
    search,
    setSearch,
    debouncedSearch,
    selectedTypes,
    toggleType,
    clearTypes,
    showExamples,
    setShowExamples,
  } = useReportFilters();

  const { data, isLoading } = useQuery(
    reportQueries.list(
      {
        search: debouncedSearch,
        types: selectedTypes,
        showExamples,
      },
      tenantData?.tenant.tenant_id,
    ),
  );
  const globalTypes = Array.from(
    new Set(
      (data ?? []).filter((item) => item.isExample).map((item) => item.type),
    ),
  );
  const reportTypes = Array.from(
    new Set(
      (data ?? [])
        .map((item) => item.type)
        .filter((type): type is string => typeof type === "string"),
    ),
  );

  return (
    <>
      <title>Reports - Researchly</title>
      <div className="flex flex-col gap-6 p-4 sm:p-6">
        <div className="flex items-center justify-between gap-3">
          <h1 className="text-2xl font-semibold tracking-tight">Reports</h1>
          <CreateReportDialog
            trigger={
              <Button className="gap-1.5">
                <PlusIcon className="size-4" />
                Create report
              </Button>
            }
          />
        </div>

        <FilterBar
          search={search}
          onSearchChange={setSearch}
          searchPlaceholder="Search reports..."
        >
          <FilterDropdown
            selectedTypes={selectedTypes}
            toggleType={toggleType}
            clearTypes={clearTypes}
            globalTypes={globalTypes}
            reportTypes={reportTypes}
          />
          <div className="flex items-center gap-2">
            <Checkbox
              id="show-examples"
              checked={showExamples}
              onCheckedChange={(checked) => setShowExamples(checked === true)}
            />
            <Label htmlFor="show-examples" className="cursor-pointer">
              Show examples
            </Label>
          </div>
        </FilterBar>

        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {isLoading && (
            <Card className="sm:col-span-2 lg:col-span-3">
              <CardHeader>
                <CardTitle className="text-base">Loading reports…</CardTitle>
                <CardDescription>Fetching your latest reports.</CardDescription>
              </CardHeader>
            </Card>
          )}

          {!isLoading &&
            (data ?? []).map((item) => (
              <ReportCard key={item.id} item={item} />
            ))}

          {!isLoading && (data?.length ?? 0) === 0 && (
            <Card className="sm:col-span-2 lg:col-span-3">
              <CardHeader>
                <CardTitle className="text-base">No reports found</CardTitle>
                <CardDescription>
                  Try adding some reports or adjusting your search or filters.
                </CardDescription>
              </CardHeader>
            </Card>
          )}
        </div>
      </div>
    </>
  );
}
